import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import LoadingSpinner from './LoadingSpinner'

/**
 * Chip-based multi-select with a floating checkbox dropdown.
 * Renders selected items as removable chips + a "+ Add" button that opens
 * a scrollable, checkbox-per-option popover. Dropdown stays open while the
 * user ticks multiple items; closes on outside click.
 *
 * The dropdown is rendered through a portal to document.body and positioned
 * with `position: fixed` from the button's on-screen rect. This is deliberate:
 * the component lives inside the shared table (`.common-table__container` has
 * `overflow: auto` for horizontal scroll + a sticky header), and an absolutely
 * positioned dropdown gets clipped by that overflow. Portalling it out of the
 * table is the only way to escape the clip. The inline top/left are positioning
 * coordinates only — all visual styling stays in effects.css.
 *
 * Parent owns the state. This component calls `onToggle(id, isSelected)`
 * so the parent can write to Firestore however it wants. See CSS class
 * `.multi-chip-picker` in src/CSS/impactStrategy/effects.css.
 *
 * Props:
 *   selected            - array of currently selected option ids
 *   options             - array of { id, label } (all selectable options)
 *   onToggle(id, isOn)  - called when a checkbox changes
 *   renderSelected(id)  - chip body renderer (e.g. <ActivityMeta id=…/>)
 *   addLabel            - text on the "+ Add" button
 *   emptyLabel          - shown when nothing is selected
 *   emptyOptionsLabel   - shown when no options are available
 *   removeAriaLabel     - aria-label on the per-chip × button
 *   loaded              - false while the option list is still loading: shows a
 *                         spinner instead of emptyLabel / emptyOptionsLabel, which
 *                         would otherwise claim absence before anything is known.
 *                         AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */
const MultiSelect = ({
    selected = [],
    options = [],
    onToggle,
    renderSelected,
    addLabel = '+ Toevoegen',
    emptyLabel = 'Geen items toegevoegd',
    emptyOptionsLabel = 'Geen opties beschikbaar',
    removeAriaLabel = 'Verwijder',
    loaded = true,
}) => {
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState(
        /** @type {{left: number, top?: number, bottom?: number, maxHeight: number} | null} */ (null),
    )
    const dropdownRef = useRef(null)
    const addButtonRef = useRef(null)

    useEffect(() => {
        if (!open) return
        const handler = (e) => {
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target)
            const inAddButton = addButtonRef.current && addButtonRef.current.contains(e.target)
            if (!inDropdown && !inAddButton) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    // Anchor the portalled dropdown under the button, and keep it aligned when
    // the page or any scroll container (e.g. the table) scrolls or resizes.
    useLayoutEffect(() => {
        if (!open) return
        const updatePosition = () => {
            if (!addButtonRef.current) return
            const rect = addButtonRef.current.getBoundingClientRect()
            const margin = 8
            const gap = 4
            const dropdownMaxWidth = 360

            // Flip above the button when there's more room up than down.
            const spaceBelow = window.innerHeight - rect.bottom - margin
            const spaceAbove = rect.top - margin
            const openUp = spaceBelow < 220 && spaceAbove > spaceBelow

            // Keep the dropdown from spilling off the right edge.
            const left = Math.max(
                margin,
                Math.min(rect.left, window.innerWidth - dropdownMaxWidth - margin),
            )

            setPosition({
                left,
                maxHeight: Math.max(120, openUp ? spaceAbove : spaceBelow),
                ...(openUp
                    ? { bottom: window.innerHeight - rect.top + gap }
                    : { top: rect.bottom + gap }),
            })
        }
        updatePosition()
        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('scroll', updatePosition, true)
            window.removeEventListener('resize', updatePosition)
        }
    }, [open])

    const selectedSet = new Set(selected)

    return (
        <div className='multi-select-wrap'>
            <div className='multi-chip-picker'>
                {selected.map(id => (
                    <span key={id} className='multi-chip-picker__chip'>
                        {renderSelected ? renderSelected(id) : id}
                        <button
                            type='button'
                            className='multi-chip-picker__remove'
                            aria-label={removeAriaLabel}
                            onClick={() => onToggle(id, true)}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {selected.length === 0 && !loaded && (
                    <LoadingSpinner className='loading-spinner--inline' />
                )}
                {selected.length === 0 && loaded && (
                    <span className='multi-chip-picker__empty'>{emptyLabel}</span>
                )}
                <button
                    ref={addButtonRef}
                    type='button'
                    className='multi-chip-picker__add'
                    onClick={() => setOpen(v => !v)}
                >
                    {addLabel}
                </button>
            </div>
            {open && position && createPortal(
                <div
                    ref={dropdownRef}
                    className='multi-chip-picker__dropdown multi-chip-picker__dropdown--portal'
                    style={position}
                >
                    {!loaded && <LoadingSpinner className='loading-spinner--inline' />}
                    {loaded && options.length === 0 && (
                        <p className='multi-chip-picker__empty'>{emptyOptionsLabel}</p>
                    )}
                    {options.map(opt => {
                        const isSelected = selectedSet.has(opt.id)
                        return (
                            <label key={opt.id} className='multi-chip-picker__option'>
                                <input
                                    type='checkbox'
                                    checked={isSelected}
                                    onChange={() => onToggle(opt.id, isSelected)}
                                />
                                <span>{opt.label}</span>
                            </label>
                        )
                    })}
                </div>,
                document.body,
            )}
        </div>
    )
}

export default MultiSelect
